import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { SkyUiPopupInstance } from '../../popup-instance';
import { SkyDeviceService } from '@skyeng/libs/shared/device';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { returnToAngularZone, DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type TState = 'open' | 'close';

const ESCAPE_KEY_CODE = 27;

@DecorateUntilDestroy()
@Component({
  selector: 'sky-ui-popup',
  templateUrl: 'popup.html',
  styleUrls: ['./popup.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('visibility', [
      transition('* => open', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
      transition('* => close', [animate('200ms', style({ opacity: 0 }))]),
    ]),
  ],
})
export class SkyUiPopupComponent implements OnInit, OnDestroy {
  @Input() hasFooter = true;
  @Input() hasHeader = true;
  @Input() closeable = true;
  @Input() forceCloseIcon = false;
  @Input() centerFooter = false;
  @Input() topPosition = false;
  @Input() marginTop: number;
  /**
   * Включить или выключить вертикальный скрол.
   * По умолчанию контент в попапе скролится, под скролбар резервируется место, из-за чего контент смещен на 6px влево относительно центра.
   * Если выключить скрол, то контент будет выровнен правильно.
   */
  @Input() hasScroll = true;

  @ViewChild('body', { read: ElementRef }) body: ElementRef<HTMLElement>;

  state: TState = 'open';
  closeIcon = require('!html-loader!image-webpack-loader?{}!./icons/ic_24_close.svg').default;
  readonly isMobile = SkyDeviceService.isMobile();

  constructor(
    @Inject(SkyUiPopupInstance) private popupInstance: SkyUiPopupInstance<unknown, unknown>,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      fromEvent<KeyboardEvent>(document, 'keyup')
        .pipe(
          filter(event => event.keyCode === ESCAPE_KEY_CODE),
          returnToAngularZone(this.ngZone),
          takeUntilDestroyed(this),
        )
        .subscribe(() => this.close());
    });

    this.popupInstance
      .onShouldClose()
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => {
        this.state = 'close';
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy(): void {}

  close(): void {
    if (this.closeable) {
      this.popupInstance.close({ rejected: true });
    }
  }

  onAnimationDone(): void {
    if (this.state === 'close') {
      this.popupInstance.detach();
    }
  }

  scrollBodyToBottom(): void {
    if (!this.body || !this.body.nativeElement) {
      return;
    }

    this.body.nativeElement.scrollTo({
      top: this.body.nativeElement.offsetHeight,
      left: 0,
      behavior: 'smooth',
    });
  }
}
